import { updateMastery } from './adaptive'
import { emptyMastery } from '../services/progressRepository'
import { wordById } from '../data/foundations'
import { addLearningDays, localLearningDate } from './reviewScheduler'
import type { Lesson } from '../types/content'
import type { AttemptDetails, ConceptLearningRecord, EvidenceCounters, LearningSkill, Progress } from '../types/progress'

export type { AttemptDetails } from '../types/progress'
export type EvidenceKind = 'checked' | 'practice'
export const emptyEvidenceCounters = (): EvidenceCounters => ({ independentCorrect: 0, independentIncorrect: 0, supportedCorrect: 0, supportedAttempts: 0, participation: 0 })
const updateMany = (record: Progress['phonemeMastery'], ids: string[], correct: boolean) => {
  const result = { ...record }
  for (const id of new Set(ids)) result[id] = updateMastery(result[id] ?? emptyMastery(), correct)
  return result
}

export function skillForLesson(lesson: Lesson): LearningSkill {
  if (lesson.type === 'segment') return 'segment_count'
  if (lesson.type === 'blend') return 'blending'
  if (lesson.mode === 'oral_only') return 'sound_discrimination'
  if (lesson.type === 'encode') return 'encoding'
  if (lesson.type === 'decode') return 'decoding'
  if (['word-challenge', 'review'].includes(lesson.type)) return 'word_recognition'
  return 'mapping'
}

/** Self-report remains participation; new independent evidence requires explicit metadata. */
export function recordLearningEvidence(current: Progress, lesson: Lesson, correct: boolean, evidence: EvidenceKind, details?: AttemptDetails, now = new Date()): Progress {
  const day = localLearningDate(now)
  const at = now.toISOString()
  const skill = details?.skill ?? skillForLesson(lesson)
  const assistanceLevel = details?.assistanceLevel ?? 0
  const attemptNumber = Math.max(1, Math.floor(details?.attemptNumber ?? 1))
  const wordTask = ['blend', 'segment', 'decode', 'encode', 'word-challenge', 'review'].includes(lesson.type)
  const wordId = details?.wordId && wordById.has(details.wordId) ? details.wordId : wordTask ? lesson.wordIds.find((id) => wordById.has(id)) : undefined
  const word = wordId ? wordById.get(wordId) : undefined
  const adultVerified = details?.adultVerified === true
  const participation = evidence === 'practice' || (skill === 'decoding' && !adultVerified)
  const independent = Boolean(details) && !participation && attemptNumber === 1 && assistanceLevel === 0
    && (details?.optionCount === undefined || details.optionCount >= 2)
  const records = current.learningRecords ?? { concepts: {}, skills: {}, words: {} }
  const previousWord = wordId ? records.words[wordId] : undefined
  // "Transfer" means a different word not previously recorded in this app. It
  // cannot establish whether the child has encountered the word elsewhere.
  const transfer = independent && correct && skill === 'decoding' && adultVerified && details?.mode === 'transfer' && Boolean(word) && !previousWord?.exposures
  const encodingTransfer = independent && correct && skill === 'encoding' && details?.mode === 'transfer' && Boolean(word) && !previousWord?.exposures
  const updateCounters = (previous: EvidenceCounters): EvidenceCounters => ({
    independentCorrect: previous.independentCorrect + Number(independent && correct),
    independentIncorrect: previous.independentIncorrect + Number(independent && !correct),
    supportedCorrect: previous.supportedCorrect + Number(!participation && !independent && correct),
    supportedAttempts: previous.supportedAttempts + Number(!participation && !independent),
    participation: previous.participation + Number(participation),
  })
  const previous = records.concepts[lesson.conceptId]
  // A successful count is an intermediate observation before the sequence task.
  // Keep its evidence, but don't overwrite the sequence's delayed-review baseline.
  const observation = details?.assessmentPhase === 'observation' && skill === 'segment_count' && correct
  let reviewIntervalDays: 1 | 3 | 7 = previous?.reviewIntervalDays ?? 1
  let lastIntervalAdvancedDate = previous?.lastIntervalAdvancedDate ?? null
  let nextReviewDate = previous?.nextReviewDate ?? addLearningDays(now, 1)
  const supportSkills = new Set(previous?.supportSkills ?? (previous?.needsSupport ? [previous.lastSkill] : []))
  if (!participation && (!independent || !correct)) {
    reviewIntervalDays = 1
    nextReviewDate = addLearningDays(now, 1)
    supportSkills.add(skill)
  } else if (independent && correct) {
    supportSkills.delete(skill)
    const sameSkillBaseline = previous && previous.independentCorrect > 0 && previous.lastIndependentSkill === skill
    if (!observation && sameSkillBaseline && details?.mode === 'review' && !previous.needsSupport && supportSkills.size === 0 && previous.nextReviewDate <= day && previous.lastIndependentDate !== day && lastIntervalAdvancedDate !== day) {
      reviewIntervalDays = previous.reviewIntervalDays === 1 ? 3 : 7
      lastIntervalAdvancedDate = day
      nextReviewDate = addLearningDays(now, reviewIntervalDays)
    } else if (!observation && (!sameSkillBaseline || previous.needsSupport || previous.nextReviewDate <= day)) {
      // Participation, a first success in a different skill, or ordinary practice
      // establishes a next-day baseline; none is evidence of delayed retention.
      reviewIntervalDays = 1
      nextReviewDate = addLearningDays(now, 1)
    }
  }
  const conceptRecord: ConceptLearningRecord = {
    ...updateCounters(previous ?? emptyEvidenceCounters()),
    lastLessonId: (participation || observation) && previous ? previous.lastLessonId : lesson.id,
    lastWordId: (participation || observation) && previous ? previous.lastWordId : wordId,
    lastSkill: (participation || observation) && previous ? previous.lastSkill : skill,
    lastPracticed: at, lastIndependentDate: independent && !observation ? day : previous?.lastIndependentDate ?? null,
    lastIndependentSkill: independent && correct && !observation ? skill : previous?.lastIndependentSkill,
    lastIntervalAdvancedDate, nextReviewDate, reviewIntervalDays, needsSupport: supportSkills.size > 0, supportSkills: [...supportSkills],
  }
  const learningRecords: Progress['learningRecords'] = {
    concepts: { ...records.concepts, [lesson.conceptId]: conceptRecord },
    skills: { ...records.skills, [skill]: updateCounters(records.skills[skill] ?? emptyEvidenceCounters()) },
    words: wordId ? { ...records.words, [wordId]: {
      exposures: (previousWord?.exposures ?? 0) + 1,
      independentDecodingCorrect: (previousWord?.independentDecodingCorrect ?? 0) + Number(independent && skill === 'decoding' && correct),
      independentDecodingIncorrect: (previousWord?.independentDecodingIncorrect ?? 0) + Number(independent && skill === 'decoding' && !correct),
      transferCorrect: (previousWord?.transferCorrect ?? 0) + Number(transfer),
      encodingTransferCorrect: (previousWord?.encodingTransferCorrect ?? 0) + Number(encodingTransfer), lastPracticed: at,
    } } : records.words,
  }
  const next: Progress = {
    ...current, learningRecords,
    attemptHistory: [...(current.attemptHistory ?? []), {
      lessonId: lesson.id, conceptId: lesson.conceptId, at, skill, assistanceLevel, attemptNumber, wordId,
      optionCount: details?.optionCount, responseMs: details?.responseMs, mode: details?.mode ?? 'lesson', adultVerified,
      assessmentPhase: observation ? 'observation' as const : 'final' as const,
      correct, evidence: participation ? 'practice' as const : evidence, independent, transfer, encodingTransfer,
    }].slice(-500),
  }
  // Legacy checked calls retain practice-score behavior. Assisted answers no longer
  // inflate old mastery, and neither old scores nor incomplete metadata imply independence.
  if (participation || (details && !independent)) return next
  const mappingCheck = ['mapping', 'encoding'].includes(skill)
  const mappingIds = skill === 'encoding' && word ? word.mapping.map((part) => part.mappingId) : lesson.targetMappingIds
  const graphemeIds = skill === 'encoding' && word ? word.graphemeIds : lesson.targetGraphemeIds
  return {
    ...next,
    phonemeMastery: skill === 'sound_discrimination' ? updateMany(current.phonemeMastery, lesson.targetPhonemeIds, correct) : current.phonemeMastery,
    graphemeMastery: mappingCheck ? updateMany(current.graphemeMastery, graphemeIds, correct) : current.graphemeMastery,
    mappingMastery: mappingCheck ? updateMany(current.mappingMastery, mappingIds, correct) : current.mappingMastery,
    blendingMastery: skill === 'blending' ? updateMastery(current.blendingMastery, correct) : current.blendingMastery,
    segmentingMastery: skill === 'segment_sequence' ? updateMastery(current.segmentingMastery, correct) : current.segmentingMastery,
    wordDecodingMastery: independent && skill === 'decoding' && wordId ? updateMany(current.wordDecodingMastery, [wordId], correct) : current.wordDecodingMastery,
  }
}
